import { Button } from '@econsult/econsult-ui/dist/components/Button';
import { FlexDiv } from 'components/Div/styles';
import { Divider } from 'components/Snomed/styles';
import React from 'react';
import { acceptTerms } from 'store/action/wallet';
import { useAppDispatch, useAppState } from 'store/configureStore';
import { Link } from 'styles/elements/Links';
import theme from 'styles/theme';
import GenericModal from './GenericModal';
import * as Styled from './styles';

const nhsLink =
  'https://www.england.nhs.uk/long-read/guidance-on-the-use-of-ai-enabled-ambient-scribing-products-in-health-and-care-settings/';

const messageOne = `Hi Scribe is an AI-powered tool designed to help you focus on what matters most: your patients. 
Our secure ambient scribe transcribes your consultations in real-time, instantly generating a summary to save you valuable time on note-taking.`;
const messageTwo = `To help you save time, we've enabled Hi Scribe on your account, with 30 minutes of transcription free for you every day. 
Additional time and passes are available for purchase directly in the application.
`;

const responsibilitiesPart1 = 'Hi Scribe is compliant with ';
const linkNHS = "NHS England's ambient scribe guidance";
const responsibilitiesPart2 = ` and is designed to
support, not replace, clinical practice. As a user, you are responsible for the following:`;

const responsibilities = [
  {
    id: 'practice-approval',
    title: 'Practice approval:',
    description:
      "NHS England's guidance includes critical high-level actions a practice should put in place to ensure safe adoption.",
  },
  {
    id: 'informing-patient',
    title: 'Informing the patient:',
    description: 'You must tell the patient that you are using an ambient scribe to assist with note-taking.',
  },
  {
    id: 'reviewing-notes',
    title: 'Reviewing notes:',
    description:
      "You must carefully review, edit, and approve all generated notes to ensure they are accurate and complete before adding them to the patient's record.",
  },
];

interface WelcomeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const WelcomeModal: React.FC<WelcomeModalProps> = ({ isOpen, onClose }) => {
  const dispatch = useAppDispatch();
  const {
    app: { selectedPractice },
    wallet: { termsAccepted },
  } = useAppState();
  const selectedPracticeId = selectedPractice?.id;

  const handleClose = () => {
    if (!termsAccepted && selectedPracticeId) {
      return acceptTerms(dispatch, selectedPracticeId);
    }
    if (termsAccepted) {
      return onClose();
    }
    return null;
  };

  return (
    <GenericModal isOpen={isOpen} onClose={handleClose} title="Welcome to Hi Scribe">
      <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        <Styled.ModalMessage>{messageOne}</Styled.ModalMessage>
        <Styled.ModalMessage>{messageTwo}</Styled.ModalMessage>
        <Divider />
        <Styled.ModalTitle>Your Responsibilities</Styled.ModalTitle>
        <Styled.ModalMessage>
          {responsibilitiesPart1}
          <Link href={nhsLink}>{linkNHS}</Link>
          {responsibilitiesPart2}
        </Styled.ModalMessage>
        <ul>
          {responsibilities.map(item => (
            <li key={item.id}>
              <strong>{item.title}</strong> {item.description}
            </li>
          ))}
        </ul>
        <Divider />
        <Styled.ModalMessage>For full details, please refer to our official documentation:</Styled.ModalMessage>
        <ul>
          <li>
            <Link href="https://help.econsult.health/en/articles/12259808-hi-scribe-dpia">
              Data protection impact assessment
            </Link>
          </li>
          <li>
            <Link href="https://help.econsult.health/en/articles/12259820-hi-scribe-clinical-safety">
              Clinical safety documentation (DCB0129)
            </Link>
          </li>
        </ul>
        <Styled.ModalMessage>
          If you have any questions or feedback, please contact us at{' '}
          <Link href="mailto:practice.support@econsult.health">practice.support@econsult.health</Link>
        </Styled.ModalMessage>
        <FlexDiv style={{ justifyContent: 'space-between' }}>
          <Styled.ButtonContainer>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  disabled={termsAccepted}
                  onChange={handleClose}
                  style={{
                    marginRight: '8px',
                    accentColor: theme.colorPrimaryPrimary100,
                    cursor: !termsAccepted ? 'pointer' : 'default',
                  }}
                />
                Accept our{' '}
                <Link
                  style={{ paddingLeft: '6px' }}
                  href="https://help.econsult.health/en/articles/12259823-hi-scribe-terms-conditions"
                >
                  terms & conditions
                </Link>
              </label>
            </div>
          </Styled.ButtonContainer>

          <Button
            size="small"
            state="primary"
            id="closeTermsModal"
            onClick={handleClose}
            disabled={!termsAccepted}
            data-testid="closeTermsModal"
            style={{ height: '42px', padding: '0 16px', fontSize: '16px', marginTop: '3px' }}
          >
            Close
          </Button>
        </FlexDiv>
      </div>
    </GenericModal>
  );
};

export default WelcomeModal;
